/**
 * El stylesheet que se inyecta al arrancar la app.
 *
 * Extraído de `theme-silia.ts` (paso 03, ver `theme/spec.md` §9). Sigue siendo
 * un template string; el paso 05 lo reemplaza por `emitVars()` generado.
 */

import {
  brand,
  surface,
  ink,
  line,
  semantic,
  tagExtras,
  emotion,
  kind,
  node,
  fill,
  elevation,
  typography,
  radius,
  space,
  control,
  tagPalette,
} from './primitives';
import { theme } from './palette';

/* ═══════════════════════════════════════════════════════════════════════
   6. CSS VARIABLES
   ═══════════════════════════════════════════════════════════════════════ */

/** Tag palette vars, in both shapes — see the note on `tagPalette`. */
const tagVars = tagPalette
  .map(
    (c) =>
      `    --silia-tag-${c.key}-bg: ${c.fill};\n` +
      `    --silia-tag-${c.key}-tag: ${c.tag};\n` +
      `    --silia-tag-${c.key}-dot: ${c.dot};\n` +
      `    --silia-tag-${c.key}-text: ${c.text};`,
  )
  .join('\n');

/** `--ant-*` aliases for the tag palette, consumed by Data Views CSS today. */
const tagVarsLegacy = tagPalette
  .map(
    (c) =>
      `    --ant-tag-${c.key}-fill: ${c.fill};\n` +
      `    --ant-tag-${c.key}-tag: ${c.tag};\n` +
      `    --ant-tag-${c.key}-dot: ${c.dot};\n` +
      `    --ant-tag-${c.key}-text: ${c.text};`,
  )
  .join('\n');

/**
 * The stylesheet injected at app start.
 *
 * Two namespaces are emitted on purpose, for the length of the migration:
 *
 *   `--silia-*`  the real token set. New and migrated code reads only these.
 *   `--ant-*`    aliases for the names `theme-silia-antiguo` already published,
 *                so the CSS that reads them today keeps painting. Ant Design
 *                also generates its own `--ant-*` at runtime; these do not
 *                collide because AntD suffixes its own (`--ant-color-primary`,
 *                camelCase vs kebab). Still — nothing new should be added to
 *                this block. It only shrinks.
 *
 * Delete an alias when the last CSS file reading it has been migrated. When the
 * block is empty, the migration is done.
 */
export const cssVariables = `
  :root {
    /* ── Brand ───────────────────────────────────────────────────────────
       Named --silia-primary rather than plain --silia-lime, because with
       cssVar on AntD
       generates its own preset ramp into this namespace, and one of those
       presets IS called lime. Same reason for --silia-type-size,
       --silia-type-line-height, --silia-ctl-height and --silia-ctl-height-sm
       further down: AntD emits --silia-font-size, --silia-line-height and
       --silia-control-height itself, and crucially it emits them UNITLESS
       (40, not 40px). A collision there is silent — whichever block loses, the
       other side gets "padding: 40" and simply stops applying. Do not
       "restore" these to the obvious name. */
    --silia-ink-strong: ${brand.black};
    --silia-primary: ${brand.lime};
    --silia-accent-bg-subtle: ${brand.n100};
    --silia-accent-bg: ${brand.n200};
    --silia-accent-border: ${brand.n400};
    --silia-accent-border-strong: ${brand.n500};
    --silia-accent-fg-muted: ${brand.n600};
    --silia-accent-fg: ${brand.n700};
    --silia-accent-fg-strong: ${brand.n800};
    --silia-accent-solid: ${brand.n900};

    /* ── Surfaces ──────────────────────────────────────────────────── */
    --silia-bg-layout: ${surface.bgLayout};
    --silia-bg-elevated: ${surface.bgElevated};
    --silia-bg-container: ${surface.bgContainer};
    --silia-surface-tint-1: ${surface.tint1};
    --silia-surface-tint-2: ${surface.tint2};
    --silia-surface-tint-3: ${surface.tint3};
    --silia-input-addon: ${surface.inputAddon};

    /* ── Text & icons ──────────────────────────────────────────────── */
    --silia-text: ${ink.text};
    --silia-text-description: ${ink.textDescription};
    --silia-text-icon: ${ink.textIcon};
    --silia-text-placeholder: ${ink.textPlaceholder};
    --silia-text-inverse: ${ink.textInverse};

    /* ── Borders ───────────────────────────────────────────────────── */
    --silia-border: ${line.border};
    --silia-border-subtle: ${line.borderSubtle};
    --silia-fill-content: ${line.fillContent};
    --silia-split: ${line.split};

    /* ── Semantic feedback ─────────────────────────────────────────── */
    --silia-success-bg: ${semantic.successBg};
    --silia-success-fg: ${semantic.successFg};
    --silia-success-fg-strong: ${semantic.successFgStrong};
    --silia-success-bd: ${semantic.successBd};
    --silia-warning-bg: ${semantic.warningBg};
    --silia-warning-fg: ${semantic.warningFg};
    --silia-warning-fg-strong: ${semantic.warningFgStrong};
    --silia-warning-bd: ${semantic.warningBd};
    --silia-danger-bg: ${semantic.dangerBg};
    --silia-danger-fg: ${semantic.dangerFg};
    --silia-danger-fg-strong: ${semantic.dangerFgStrong};
    --silia-danger-bd: ${semantic.dangerBd};
    --silia-info-bg: ${semantic.infoBg};
    --silia-info-fg: ${semantic.infoFg};
    --silia-info-fg-strong: ${semantic.infoFgStrong};
    --silia-info-bd: ${semantic.infoBd};

    /* ── Tag palette extras ────────────────────────────────────────── */
    --silia-tag-red-fg: ${tagExtras.redFg};
    --silia-tag-blue-fg: ${tagExtras.blueFg};
    --silia-tag-amber-hue: ${tagExtras.amberHue};
    --silia-tag-cyan-bg: ${tagExtras.cyanBg};
    --silia-tag-cyan-fg: ${tagExtras.cyanFg};
    --silia-tag-cyan-hue: ${tagExtras.cyanHue};
    --silia-tag-orange-bg: ${tagExtras.orangeBg};
    --silia-tag-orange-fg: ${tagExtras.orangeFg};
    --silia-tag-orange-hue: ${tagExtras.orangeHue};
    --silia-tag-pink-bg: ${tagExtras.pinkBg};
    --silia-tag-pink-fg: ${tagExtras.pinkFg};
    --silia-tag-pink-hue: ${tagExtras.pinkHue};
    --silia-tag-brown-bg: ${tagExtras.brownBg};
    --silia-tag-brown-fg: ${tagExtras.brownFg};
    --silia-tag-brown-hue: ${tagExtras.brownHue};

    /* ── Tag palette weights (fill / tag / dot / text) ──────────────── */
${tagVars}

    /* ── Emotion avatars ───────────────────────────────────────────── */
    --silia-emotion-positive-bg: ${emotion.positiveBg};
    --silia-emotion-positive-fg: ${emotion.positiveFg};
    --silia-emotion-neutral-bg: ${emotion.neutralBg};
    --silia-emotion-neutral-fg: ${emotion.neutralFg};
    --silia-emotion-negative-bg: ${emotion.negativeBg};
    --silia-emotion-negative-fg: ${emotion.negativeFg};

    /* ── Object kind ───────────────────────────────────────────────────
       Declared as literals, not as var() aliases. A var() alias resolves in
       the element where it is DECLARED, so an alias written only in :root
       would stay frozen on Silia's purple even after a template remaps it —
       which is why the prototype redeclares these inside every template. */
    --silia-kind-folder-bg: ${kind.folderBg};
    --silia-kind-folder-fg: ${kind.folderFg};
    --silia-kind-folder-bd: ${kind.folderBd};
    --silia-kind-agent-bg: ${kind.agentBg};
    --silia-kind-agent-fg: ${kind.agentFg};
    --silia-kind-agent-bd: ${kind.agentBd};
    --silia-kind-agent-soft-bg: ${kind.agentSoftBg};
    --silia-kind-agent-soft-fg: ${kind.agentSoftFg};
    --silia-kind-agent-soft-bd: ${kind.agentSoftBd};
    --silia-kind-qa-bg: ${kind.qaBg};
    --silia-kind-qa-fg: ${kind.qaFg};
    --silia-kind-qa-bd: ${kind.qaBd};
    --silia-kind-rta-bg: ${kind.rtaBg};
    --silia-kind-rta-fg: ${kind.rtaFg};
    --silia-kind-rta-bd: ${kind.rtaBd};
    --silia-kind-data-view-bg: ${kind.dataViewBg};
    --silia-kind-data-view-fg: ${kind.dataViewFg};
    --silia-kind-data-view-bd: ${kind.dataViewBd};
    --silia-kind-dashboard-bg: ${kind.dashboardBg};
    --silia-kind-dashboard-fg: ${kind.dashboardFg};
    --silia-kind-dashboard-bd: ${kind.dashboardBd};
    --silia-kind-file-bg: ${kind.fileBg};
    --silia-kind-file-fg: ${kind.fileFg};
    --silia-kind-file-bd: ${kind.fileBd};

    /* ── Canvas node type ──────────────────────────────────────────── */
    --silia-node-tool-bg: ${node.toolBg};
    --silia-node-tool-fg: ${node.toolFg};
    --silia-node-tool-bd: ${node.toolBd};
    --silia-node-mcp-bg: ${node.mcpBg};
    --silia-node-mcp-fg: ${node.mcpFg};
    --silia-node-mcp-bd: ${node.mcpBd};
    --silia-node-table-bg: ${node.tableBg};
    --silia-node-table-fg: ${node.tableFg};
    --silia-node-table-bd: ${node.tableBd};
    --silia-node-agent-bg: ${node.agentBg};
    --silia-node-agent-fg: ${node.agentFg};
    --silia-node-agent-bd: ${node.agentBd};

    /* ── Translucent fills & scrim ─────────────────────────────────── */
    --silia-fill-1: ${fill.f1};
    --silia-fill-2: ${fill.f2};
    --silia-fill-3: ${fill.f3};
    --silia-fill-brand-1: ${fill.brand1};
    --silia-fill-brand-2: ${fill.brand2};
    --silia-fill-inverse-1: ${fill.inverse1};
    --silia-fill-inverse-2: ${fill.inverse2};
    --silia-scrim: ${fill.scrim};
    --silia-scrim-strong: ${fill.scrimStrong};
    --silia-primary-bg-subtle: ${fill.limeSoft};
    --silia-primary-border: ${fill.limeBorder};
    --silia-primary-bg: ${fill.limeTenue};

    /* ── Elevation ─────────────────────────────────────────────────── */
    --silia-shadow-sm: ${elevation.sm};
    --silia-shadow-md: ${elevation.md};
    --silia-shadow-lg: ${elevation.lg};
    --silia-shadow-xl: ${elevation.xl};
    --silia-focus-ring: ${elevation.focusRing};
    --silia-shadow-glow: ${elevation.glow};
    --silia-shadow-cta: ${elevation.cta};
    --silia-shadow-cta-hover: ${elevation.ctaHover};

    /* ── Typography ────────────────────────────────────────────────── */
    --silia-font-sans: ${typography.fontSans};
    --silia-font-mono: ${typography.fontMono};
    --silia-type-size: ${typography.fontSize}px;
    --silia-font-size-icons: ${typography.fontSizeIcons}px;
    --silia-type-line-height: ${typography.lineHeight};

    /* ── Shape ─────────────────────────────────────────────────────── */
    --silia-radius-xs: ${radius.xs}px;
    --silia-radius-sm: ${radius.sm}px;
    --silia-radius-md: ${radius.md}px;
    --silia-radius-lg: ${radius.lg}px;
    --silia-radius-pill: ${radius.pill}px;
    --silia-radius-circle: ${radius.circle};

    /* ── Space ─────────────────────────────────────────────────────── */
    --silia-space-xxs: ${space.xxs}px;
    --silia-space-xs: ${space.xs}px;
    --silia-space-sm: ${space.sm}px;
    --silia-space-md: ${space.md}px;
    --silia-space-lg: ${space.lg}px;
    --silia-space-xl: ${space.xl}px;
    --silia-space-xxl: ${space.xxl}px;

    /* ── Controls ──────────────────────────────────────────────────── */
    --silia-ctl-height: ${control.height}px;
    --silia-ctl-height-sm: ${control.heightSM}px;

    /* ── Page shell ────────────────────────────────────────────────────
       The chrome every listing screen repeats: a padded page, a breadcrumb,
       and one white card holding the title, the toolbar and the content.

       Twelve screens had built it by hand — org-page, skills-page__card,
       wizard__shell, overview-shell__card, users-card, details-card,
       assistant-management-card, metrics-header-card and four bare AntD
       Cards — between them using six card paddings, five title sizes and
       four breadcrumb gaps. These are the numbers Organizations settled on,
       which are the ones already on the scale.

       The responsive steps are here too: AntD has no breakpoint token, so a
       stylesheet still writes the media query, but it reads the value from
       here instead of inventing 20px and 10px, neither of which is on any
       scale. */
    --silia-page-padding: ${space.lg}px;
    --silia-page-padding-tablet: ${space.md}px;
    --silia-page-padding-mobile: ${space.sm}px;
    --silia-page-gap: ${space.md}px;
    --silia-page-card-padding: ${space.lg}px;
    --silia-page-card-padding-tablet: ${space.md}px;
    --silia-page-card-gap: ${space.lg}px;
    --silia-page-card-gap-tablet: ${space.md}px;

    /* ── Headings ──────────────────────────────────────────────────────
       AntD emits its own --silia-font-size-heading-N, but scoped to the
       .silia class it puts on its components — a stylesheet reading them
       from a plain element resolves nothing, silently. These are the same
       numbers under a name of ours, safe to read anywhere.

       --silia-title-* rather than --silia-heading-*: AntD also generates
       --silia-font-size-heading-N and --silia-line-height-heading-N, and a
       collision in that namespace is invisible. */
    --silia-title-1: ${typography.fontSizeHeading1}px;
    --silia-title-2: ${typography.fontSizeHeading2}px;
    --silia-title-3: ${typography.fontSizeHeading3}px;
    --silia-title-4: ${typography.fontSizeHeading4}px;
    --silia-title-5: ${typography.fontSizeHeading5}px;
    --silia-title-1-lh: ${Math.round(typography.fontSizeHeading1 * typography.lineHeightHeading1)}px;
    --silia-title-2-lh: ${Math.round(typography.fontSizeHeading2 * typography.lineHeightHeading2)}px;
    --silia-title-3-lh: ${Math.round(typography.fontSizeHeading3 * typography.lineHeightHeading3)}px;
    --silia-title-4-lh: ${Math.round(typography.fontSizeHeading4 * typography.lineHeightHeading4)}px;
    --silia-title-5-lh: ${Math.round(typography.fontSizeHeading5 * typography.lineHeightHeading5)}px;

    /* ── Brand assets ──────────────────────────────────────────────── */
    --silia-logo-url: ${theme.logoUrl};
    --silia-logo-mobile-url: ${theme.logoMobileUrl};
    --silia-background-url: ${theme.backgroundUrl};
    --silia-background-url-webp: ${theme.backgroundUrlWebp};
    --silia-hero-background-url: ${theme.heroBackgroundUrl};
    --silia-hero-background-url-webp: ${theme.heroBackgroundUrlWebp};
    --silia-modules-background-url: ${theme.modulesBackgroundUrl};
    --silia-modules-background-url-webp: ${theme.modulesBackgroundUrlWebp};

    /* ═══════════════════════════════════════════════════════════════════
       LEGACY --ant-* ALIASES — migration scaffolding, delete as you go.
       Every name below existed in theme-silia-antiguo. Nothing new goes here.
       ═══════════════════════════════════════════════════════════════════ */
    --ant-colorPrimary: ${theme.colorPrimary};
    --ant-colorSecondary: ${theme.colorSecondary};
    --ant-colorTertiary: ${theme.colorTertiary};
    --ant-colorAvatar: ${theme.colorAvatar};
    --ant-colorIcon: ${theme.colorIcon};
    --ant-colorPrimaryBorderHover: ${theme.colorPrimaryBorderHover};
    --ant-colorPrimaryBgHover: ${theme.colorPrimaryBgHover};
    --ant-colorText: ${theme.colorText};
    --ant-colorTextPrimary: ${theme.colorTextPrimary};
    --ant-colorTextDescription: ${theme.colorTextDescription};
    --ant-colorTextSecondary: ${theme.colorTextSecondary};
    --ant-colorTextTertiary: ${theme.colorTextIcon};
    --ant-colorTextQuaternary: ${theme.colorTextPlaceholder};
    --ant-colorTextMuted: ${theme.colorTextPlaceholder};
    --ant-colorTextButton: ${theme.colorTextButton};
    --ant-colorBgContainer: ${theme.colorBgContainer};
    --ant-colorBgElevated: ${theme.colorBgElevated};
    --ant-colorBgDisabled: ${theme.colorBgDisabled};
    --ant-layoutBg: ${theme.layoutBg};
    --ant-colorBorder: ${theme.colorBorder};
    --ant-colorBorderSecondary: ${theme.colorBorderSecondary};
    --ant-colorSplit: ${theme.colorSplit};
    --ant-colorBgTextHover: ${theme.colorBgTextHover};
    --ant-colorFillSecondary: ${theme.colorFillSecondary};
    --ant-colorFillTertiary: ${theme.colorFillTertiary};
    --ant-colorFillQuaternary: ${theme.colorFillQuaternary};
    --ant-colorError: ${theme.colorError};
    --ant-colorErrorBg: ${theme.colorErrorBg};
    --ant-colorStatusUnknown: ${theme.colorStatusUnknown};
    --ant-colorStatusUnknownBg: ${theme.colorStatusUnknownBg};
    --ant-colorStatusUnknownBorder: ${theme.colorStatusUnknownBorder};
    --ant-colorBgTag: ${theme.colorBgTag};
    --ant-colorTagText: ${theme.colorTagText};
    --ant-colorTagBorder: ${theme.colorTagBorder};
    --ant-colorIconInbox: ${theme.colorIconInbox};
    --ant-colorIconWidget: ${theme.colorIconWidget};
    --ant-colorCardHover: ${theme.colorCardHover};
    --ant-colorCardShadow: ${theme.colorCardShadow};
    --ant-colorCardShadowHover: ${theme.colorCardShadowHover};
    --ant-colorAccountTextStrong: ${theme.colorAccountTextStrong};
    --ant-colorAccountTextMuted: ${theme.colorAccountTextMuted};
    --ant-colorAccountBorderSubtle: ${theme.colorAccountBorderSubtle};
    --ant-colorAccountBgElevated: ${theme.colorAccountBgElevated};
    --ant-colorAccountBgPanel: ${theme.colorAccountBgPanel};
    --ant-colorNeutralTextDescription: ${theme.colorNeutralTextDescription};
    --ant-colorNeutralTextPlaceholder: ${theme.colorNeutralTextPlaceholder};
    --ant-colorNeutralBorder: ${theme.colorNeutralBorder};
    --ant-colorNeutralSurfaceTint: ${theme.colorNeutralSurfaceTint};
    --ant-colorNeutralSurfaceTintStrong: ${theme.colorNeutralSurfaceTintStrong};
${tagVarsLegacy}
    --ant-colorHomeBgLayout: ${theme.colorHomeBgLayout};
    --ant-colorHomeBgCard: ${theme.colorHomeBgCard};
    --ant-colorHomeSplit: ${theme.colorHomeSplit};
    --ant-colorHomeBorder: ${theme.colorHomeBorder};
    --ant-colorHomeText: ${theme.colorHomeText};
    --ant-colorHomeTextDescription: ${theme.colorHomeTextDescription};
    --ant-colorHomeTextIcon: ${theme.colorHomeTextIcon};

    /* Alias de acento en el namespace --ant-*, con nombre de rol.
       Purple2 y Purple8 no existían: se consumían con un fallback literal que
       congelaba el violeta de Silia con cualquier marca activa. */
    --ant-colorAccentBgSubtle: ${theme.colorAccentBgSubtle};
    --ant-colorAccentBg: ${theme.colorAccentBg};
    --ant-colorAccentBorder: ${theme.colorAccentBorder};
    --ant-colorAccentBorderStrong: ${theme.colorAccentBorderStrong};
    --ant-colorAccentFg: ${theme.colorAccentFg};
    --ant-colorAccentFgStrong: ${theme.colorAccentFgStrong};
    --ant-colorPrimaryBrand: ${theme.colorPrimaryBrand};
    --ant-colorInkStrong: ${theme.colorInkStrong};
    --ant-colorHomeFillContent: ${theme.colorHomeFillContent};
    --ant-colorHomeEmptyPrimary: ${theme.colorHomeEmptyPrimary};
    --ant-colorHomeEmptyPrimaryBorder: ${theme.colorHomeEmptyPrimaryBorder};
    --ant-colorHomeEmptyCtaBg: ${theme.colorHomeEmptyCtaBg};
    --ant-colorHomeEmptyCtaQuietBg: ${theme.colorHomeEmptyCtaQuietBg};
    --ant-colorHomeEmptyCtaQuietHover: ${theme.colorHomeEmptyCtaQuietHover};
    --ant-colorHomeEmptyTemplateHover: ${theme.colorHomeEmptyTemplateHover};
    --ant-colorTopBarBg: ${theme.colorTopBarBg};
    --ant-colorTopBarBorder: ${theme.colorTopBarBorder};
    --ant-colorTopBarText: ${theme.colorTopBarText};
    --ant-colorTopBarBadgeBg: ${theme.colorTopBarBadgeBg};
    --ant-colorTopBarBadgeBorder: ${theme.colorTopBarBadgeBorder};
    --ant-colorTopBarBadgeText: ${theme.colorTopBarBadgeText};
    --ant-colorTopBarReturnHover: ${theme.colorTopBarReturnHover};
    --ant-colorAssistantBadgeBg: ${theme.colorAssistantBadgeBg};
    --ant-colorAssistantBadgeText: ${theme.colorAssistantBadgeText};
    --ant-colorAssistantBadgeBorder: ${theme.colorAssistantBadgeBorder};
    --ant-colorMetricsBarFill: ${theme.colorMetricsBarFill};
    --ant-colorMetricsLine: ${theme.colorMetricsLine};
    --ant-colorMetricsEscalations: ${theme.colorMetricsEscalations};
    --ant-colorMetricsMessages: ${theme.colorMetricsMessages};
    --ant-colorMetricsCategory1: ${theme.colorMetricsCategory1};
    --ant-colorMetricsCategory2: ${theme.colorMetricsCategory2};
    --ant-colorMetricsCategory3: ${theme.colorMetricsCategory3};
    --ant-colorMetricsCategory4: ${theme.colorMetricsCategory4};
    --ant-colorMetricsCategory5: ${theme.colorMetricsCategory5};
    --ant-colorTitleLogin: ${theme.colorTitleLogin};
    --ant-colorIconLogin: ${theme.colorIconLogin};
    --ant-heroTextColor: ${theme.heroTextColor};
    --ant-appName: ${theme.appName};
    --ant-fontSizeIcons: ${theme.fontSizeIcons}px;
    --ant-borderRadiusXS: ${theme.borderRadiusXS}px;
    --ant-borderRadiusSM: ${theme.borderRadiusSM}px;
    --ant-paddingXXL: ${theme.paddingXXL}px;
    --ant-paddingXL: ${theme.paddingXL}px;
    --ant-paddingLG: ${theme.paddingLG}px;
    --ant-paddingSM: ${theme.paddingSM}px;
    --ant-paddingXS: ${theme.paddingXS}px;
    --ant-marginXL: ${theme.marginXL}px;
    --ant-marginLG: ${theme.marginLG}px;
    --ant-marginSM: ${theme.marginSM}px;
    --ant-marginXS: ${theme.marginXS}px;
    --ant-gapXL: ${theme.gapXL}px;
    --ant-gapLG: ${theme.gapLG}px;
    --ant-gapSM: ${theme.gapSM}px;
    --ant-gapXS: ${theme.gapXS}px;
    --ant-backgroundUrl: ${theme.backgroundUrl};
    --ant-backgroundUrlWebp: ${theme.backgroundUrlWebp};
    --ant-logoUrl: ${theme.logoUrl};
    --ant-logoMobileUrl: ${theme.logoMobileUrl};
    --ant-heroBackgroundUrl: ${theme.heroBackgroundUrl};
    --ant-heroBackgroundUrlWebp: ${theme.heroBackgroundUrlWebp};
    --ant-heroBackgroundSrc: ${theme.heroBackgroundSrc};
    --ant-heroBackgroundSrcWebp: ${theme.heroBackgroundSrcWebp};
    --ant-heroBackgroundSrcWebpMobile: ${theme.heroBackgroundSrcWebpMobile};
    --ant-modulesBackgroundUrl: ${theme.modulesBackgroundUrl};
    --ant-modulesBackgroundUrlWebp: ${theme.modulesBackgroundUrlWebp};
  }

  .ant-typography,
  .ant-typography h1,
  .ant-typography h2,
  .ant-typography h3,
  .ant-typography h4,
  .ant-typography h5,
  .ant-typography h6,
  .ant-typography p {
    margin: 0 !important;
  }

  /* The dropdown shell carries a border.

     AntD gives the popup a shadow but no outline, so on the elevated off-white
     it floats without an edge — against a card of a similar tone the boundary
     reads as soft rather than as a separate surface. The border closes the
     rectangle. It is a rule because the shell exposes no border token; the
     radius and background are tokens (Dropdown.borderRadiusLG /
     colorBgElevated) and stay there.

     Padding on the menu itself, so the first and last rows are not flush
     against the border. */
  .ant-dropdown-menu {
    border: 1px solid var(--silia-border);
  }

  /* The row's height is a floor with the content centred in it, which is how the
     account-table menu is built. Setting it as vertical padding instead makes
     the row grow when a label wraps; a floor keeps every row the same height and
     lets a long one grow only when it has to.

     The horizontal inset is controlPaddingHorizontal, which is the token AntD
     writes into the row's own padding. Margin is not an option here: AntD
     declares margin:0 on this same selector at three classes, so a rule of
     two loses — an earlier attempt at margin-inline never applied at all. */
  .ant-dropdown-menu-item,
  .ant-dropdown-menu-submenu-title {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  /* The destructive row fills on hover.

     AntD has tokens for the danger colour at rest and on hover, but the only
     one that paints a BACKGROUND is dangerItemActiveBg, which it applies on
     :active — the click, not the hover. So the fill has to be a rule.

     It has to outrank AntD's own, and AntD does not play small: its generated
     selector carries ten classes and two :not() pseudo-classes
     (.ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item.ant-dropdown-menu-item-danger
     plus two :not() clauses),
     and its stylesheet is injected after this one. A two-class rule here loses
     silently — it does not even show as overridden in devtools, it just never
     applies. Hence !important, which is the one thing that clears a
     ten-class selector without racing it on count.

     The label and the icon are separate elements painting with their own
     colour, and the icon's glyph is an SVG filling from currentColor, so all
     of them have to be named or the row turns red with red text on it.

     The fill is the DARK red step. White on #ff4d4f measures 3.27:1, under the
     4.5 floor; on #cf1322 it is 5.57:1. And it has to be filled at all — white
     on the menu's ordinary hover grey (#eeeeec) is 1.16:1, an invisible label.

     This replaced the same block hand-written in three stylesheets, one of which
     (KnowledgeList.css) was BOTH unscoped and wrong: it forced the danger colour
     to stay red on hover, app-wide, which painted red text on the red fill. */
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover,
  .ant-dropdown-menu-submenu .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
    background-color: var(--silia-danger-fg-strong) !important;
  }

  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
    .ant-dropdown-menu-title-content,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
    .ant-dropdown-menu-item-icon,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
    .ant-dropdown-menu-item-icon svg,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover .anticon,
  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover .anticon svg {
    color: var(--silia-text-inverse) !important;
    fill: currentColor !important;
  }

  /* The tooltip bubble centres its label.

     AntD gives the inner box a \`minHeight\` but leaves it a plain block, so
     whenever the floor is taller than the text — a short tip, or one whose
     content is shorter than the line box — the slack collects at the bottom and
     the label rides high. Scoping \`controlHeight\` to 32 (see the Tooltip
     entry) fixes the common one-line case by making the content taller than the
     floor; this makes it hold for every case, including two-line tips and the
     ones that carry an icon. */
  .ant-tooltip-inner {
    display: flex;
    align-items: center;
  }

  /* The Segmented row height. It lives here rather than in the component token
     block because for Segmented \`controlHeight\` is an alias token — set inside
     \`components.Segmented\` it resolves to the global 40 and the row stays tall.
     32 is the toolbar height, which is what a view switch beside a title wants.

     This one rule replaced the same block hand-written in nine stylesheets, in
     four different mechanisms (CSS, a nested ConfigProvider, inline \`style\`,
     and a "canonical" wrapper duplicated across two features), with four
     different track greys between them. */
  .ant-segmented .ant-segmented-item-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    line-height: 32px;
  }

  /* size="large" is the opt-out, for the few rows that are a form control
     rather than a view switch. */
  .ant-segmented-lg .ant-segmented-item-label {
    min-height: 40px;
    line-height: 40px;
  }

  /* AntD paints its \`Tag\` text with the semantic FILL colour — a value meant
     for an icon, not for 9-12px text on the soft background. Measured, that
     lands between 2.47:1 and 3.66:1. These push each one to its dark step,
     which is the same rule the data palette follows. */
  .ant-tag-success { color: var(--silia-success-fg-strong); }
  .ant-tag-error { color: var(--silia-danger-fg-strong); }
  .ant-tag-warning { color: var(--silia-warning-fg-strong); }
  .ant-tag-processing { color: var(--silia-info-fg-strong); }
  .ant-tag-default,
  .ant-tag:not([class*='ant-tag-']) { color: var(--silia-text-description); }
`;

/**
 * El stylesheet con los alias de rol del paso 04 (fase 1) agregados.
 *
 * Los dos nombres resuelven al mismo valor, así que durante la fase 2 no existe
 * la ventana en la que romper sea posible. Ver `roleAliases.ts`.
 */
